import { create } from 'zustand';
import { FileStat, IFileSystem } from '../api/IFileSystem';
import { WebRTCSystem } from '../api/WebRTCSystem';
import { fileDownloadService } from '../../../core/FileDownloadService';
import { fileTransferService } from '../../../core/FileTransferService';
import { getParentPath, getBaseName, joinPath, detectSeparator } from '../utils/pathUtils';
import { t } from '../i18n';
import { useFileSearchStatusStore } from './useFileSearchStatus';
import { eventBus } from '../../../core/EventBus';

// 全局搜索单次请求上限。返回条数达到此值即视为被截断（可能还有更多匹配未返回）。
const GLOBAL_SEARCH_LIMIT = 200;

// 文件管理器「上次状态」持久化：按 peer 区分(不同 host 各存各的)，记录目录 + 视图模式，
// 下次打开文件管理器时恢复，避免每次都回到桌面。全程 try/catch，localStorage 不可用也不崩。
type FmViewMode = 'grid' | 'list' | 'details' | 'treemap';
const FM_VIEW_MODES: FmViewMode[] = ['grid', 'list', 'details', 'treemap'];
const FM_STATE_PREFIX = 'fm:state:';
const fmStateKey = (): string => {
  try {
    const peer = new URLSearchParams(window.location.search).get('peer') || 'default';
    return FM_STATE_PREFIX + peer;
  } catch {
    return FM_STATE_PREFIX + 'default';
  }
};
const saveFmState = (path: string, viewMode: string): void => {
  try {
    localStorage.setItem(fmStateKey(), JSON.stringify({ path, viewMode }));
  } catch { /* localStorage 不可用：忽略 */ }
};
const loadFmState = (): { path: string; viewMode: FmViewMode } | null => {
  try {
    const raw = localStorage.getItem(fmStateKey());
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || typeof o.path !== 'string') return null;
    const vm = FM_VIEW_MODES.includes(o.viewMode) ? o.viewMode : 'grid';
    return { path: o.path, viewMode: vm };
  } catch {
    return null;
  }
};

export interface UploadTask {
  id: string;
  batchId: string;
  batchRootPath: string;
  file?: File;
  name: string;
  size: number;
  path: string;
  progress: number;
  uploadedBytes?: number;  // 已上传字节数
  speed?: number;          // 当前上传速度 B/s（滑动窗口估算）
  status: 'pending' | 'uploading' | 'paused' | 'success' | 'error' | 'cancelled';
  error?: string;
  isDirectory: boolean;
  timestamp: number;
}

// Pagination constants
const PAGE_SIZE = 100; // Load 100 items per page

interface FileManagerState {
  fs: IFileSystem;
  currentPath: string;
  files: FileStat[];
  selectedPaths: string[];
  viewMode: 'grid' | 'list' | 'details' | 'treemap';
  isLoading: boolean;

  // Pagination state
  totalFiles: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  
  // Actions
  setSelectedPaths: (paths: string[]) => void;
  setCurrentPath: (path: string, addToHistory?: boolean) => Promise<boolean>;
  refresh: () => Promise<void>;
  setViewMode: (mode: 'grid' | 'list' | 'details' | 'treemap') => void;
  goBack: () => Promise<void>;
  goForward: () => Promise<void>;
  goUp: () => Promise<void>;
  
  // File Operations
  createNewFolder: (parentPath: string) => Promise<void>;
  startRenaming: (path: string) => void;
  cancelRenaming: () => void;
  endRenaming: (oldPath: string, newName: string) => Promise<void>;
  deleteFiles: (paths: string[]) => Promise<void>;
  copyFiles: (cut: boolean) => void;
  pasteFiles: () => Promise<void>;
  uploadFiles: (items: { file?: File, relativePath?: string, isDirectory?: boolean }[], targetPath?: string) => Promise<void>;

  // Upload trigger for context menu (to reuse CommandBar's file inputs)
  uploadTrigger: { type: 'file' | 'folder', targetPath: string } | null;
  triggerUpload: (type: 'file' | 'folder', targetPath: string) => void;
  clearUploadTrigger: () => void;

  // Upload Task Management
  uploadTasks: UploadTask[];
  isUploadManagerOpen: boolean; // Added
  toggleUploadManager: () => void; // Added
  openUploadManager: () => void;
  addUploadTasks: (tasks: UploadTask[], autoOpen?: boolean) => void;
  updateUploadTask: (id: string, updates: Partial<UploadTask>) => void;
  cancelUploadTask: (id: string) => void;
  retryUploadTask: (id: string) => void;
  requestCancelBatch: (batchId: string) => void; // Added
  resolveCancelBatch: (choice: 'yes' | 'no' | 'cancel') => void; // Added
  cancelBatchConfirmation: { batchId: string } | null; // Added
  cancelledBatches: Set<string>; // Added
  refreshedBatches: Set<string>;
  cancelBatch: (batchId: string, deleteUploaded: boolean) => Promise<void>; // Added
  retryBatch: (batchId: string) => void; // Added
  clearCompletedTasks: () => void;
  processQueue: () => void;
  scheduleRefresh: () => void;

  // Advanced Actions (Mock)
  downloadFiles: (paths: string[]) => void;
  copyLink: (path: string) => void;
  copyPath: (path: string) => void;
  unzip: (path: string, dest?: string) => Promise<void>;
  
  // Search State
  searchQuery: string;
  searchSettings: {
      regex: boolean;
      caseSensitive: boolean;
      wholeWord: boolean;
  };
  setSearchQuery: (query: string) => void;
  setSearchSettings: (settings: Partial<FileManagerState['searchSettings']>) => void;

  // Global Search State (NTFS MFT-level search)
  globalSearchMode: 'prefix' | 'contains';
  globalSearchQuery: string;
  globalSearchResults: Array<{
      volume: string;
      frn: number;
      name: string;
      is_dir: boolean;
      file_size: number;
      allocated_size: number;
      full_path: string;
  }>;
  globalSearchLoading: boolean;
  globalSearchError: string | null;
  globalSearchStatus: 'initializing' | 'query_too_short' | 'error' | null;
  // 本次搜索结果是否被上限截断（返回条数达到 GLOBAL_SEARCH_LIMIT），用于提示用户细化关键词
  globalSearchTruncated: boolean;
  isGlobalSearchOpen: boolean;
  setGlobalSearchMode: (mode: 'prefix' | 'contains') => void;
  setGlobalSearchQuery: (query: string) => void;
  executeGlobalSearch: () => Promise<void>;
  closeGlobalSearch: () => void;
  openGlobalSearch: () => void;
  navigateToSearchResult: (path: string) => void;

  // 文件管理器窗口是否已打开（由 App 的 showFileManager 同步而来，供搜索导航判断是否需开窗）
  isFileManagerOpen: boolean;
  setFileManagerOpen: (open: boolean) => void;
  // 待导航路径：从已关闭的文件管理器外触发搜索导航时暂存，由 initializeDataChannel 在挂载初始化后消费
  pendingNavPath: string | null;

  // Search Engine Init Modal State
  isSearchEngineInitModalOpen: boolean;
  isSearchEngineReinit: boolean; // true if reinitializing
  openSearchEngineInitModal: (isReinit?: boolean) => void;
  closeSearchEngineInitModal: () => void;

  // Internal State
  clipboard: { paths: string[], cut: boolean } | null;
  renamingPath: string | null;
  sortConfig: { key: 'name' | 'size' | 'updatedAt' | 'type', direction: 'asc' | 'desc' };
  previewFile: FileStat | null;
  editorFile: FileStat | null;
  refreshTimer: number | null;
  
  // Conflict State
  conflictState: { 
      sourceFile: string; 
      targetPath: string; 
      resolver: (action: 'overwrite' | 'rename' | 'skip', applyToAll: boolean) => void; 
  } | null;
  conflictPolicy: 'ask' | 'overwrite_all' | 'rename_all' | 'skip_all';
  
  resolveConflict: (action: 'overwrite' | 'rename' | 'skip', applyToAll: boolean) => void;
  resetConflictPolicy: () => void;
  
  // Helper for internal ops
  checkConflictsBatch: (ops: { source: string, dest: string }[]) => Promise<{ source: string, dest: string }[]>;

  // Folder Picker State
  folderPickerState: {
      isOpen: boolean;
      title?: string;
      onConfirm: (path: string) => void;
  } | null;
  openFolderPicker: (title: string, onConfirm: (path: string) => void) => void;
  closeFolderPicker: () => void;

  // Share State
  // 当前会话 peerId（由 App 经 FileManager 传入，用于分享时解析 seat）
  peerId: string | null;
  setPeerId: (peerId: string | null) => void;
  // 分享弹窗目标路径（非空时 ShareModal 显示）
  shareTargets: string[] | null;
  openShareDialog: (paths: string[]) => void;
  closeShareDialog: () => void;

  // Quick Access State
  quickAccessItems: FileStat[];
  refreshQuickAccess: () => Promise<void>;
  pinFolder: (path: string) => Promise<void>;
  unpinFolder: (path: string) => Promise<void>;

  // History State
  history: string[];
  historyIndex: number;

  initializeDataChannel: (dc: RTCDataChannel) => void;

  setSortConfig: (config: { key: 'name' | 'size' | 'updatedAt' | 'type', direction: 'asc' | 'desc' }) => void;
  openPreview: (file: FileStat) => void;
  closePreview: () => void;
  openEditor: (file: FileStat) => void;
  closeEditor: () => void;
}

export const useFileManagerStore = create<FileManagerState>((set, get) => ({
  fs: new WebRTCSystem(),
  currentPath: 'root',
  files: [],
  selectedPaths: [],
  viewMode: 'grid',
  isLoading: false,

  // Streaming/pagination state
  totalFiles: 0,
  hasMore: false,
  isLoadingMore: false,

  clipboard: null,
  renamingPath: null,
  sortConfig: { key: 'name', direction: 'asc' },
  searchQuery: '',
  searchSettings: { regex: false, caseSensitive: false, wholeWord: false },
  previewFile: null,
  editorFile: null,
  refreshTimer: null,

  // Global search initial state
  globalSearchMode: 'prefix',
  globalSearchQuery: '',
  globalSearchResults: [],
  globalSearchLoading: false,
  globalSearchError: null,
  globalSearchStatus: null,
  globalSearchTruncated: false,
  isGlobalSearchOpen: false,

  // 文件管理器窗口开关 / 待导航路径
  isFileManagerOpen: false,
  setFileManagerOpen: (open) => set({ isFileManagerOpen: open }),
  pendingNavPath: null,

  // Search Engine Init Modal initial state
  isSearchEngineInitModalOpen: false,
  isSearchEngineReinit: false,
  openSearchEngineInitModal: (isReinit = false) => set({ isSearchEngineInitModalOpen: true, isSearchEngineReinit: isReinit }),
  closeSearchEngineInitModal: () => set({ isSearchEngineInitModalOpen: false, isSearchEngineReinit: false }),

  conflictState: null,
  conflictPolicy: 'ask',
  quickAccessItems: [],
  history: ['root'],
  historyIndex: 0,
  
  uploadTasks: [],
  isUploadManagerOpen: false, // Initial state
  cancelBatchConfirmation: null,
  cancelledBatches: new Set(),
  refreshedBatches: new Set(),
  
  folderPickerState: null,

  // Share initial state
  peerId: null,
  setPeerId: (peerId) => set({ peerId }),
  shareTargets: null,
  openShareDialog: (paths) => set({ shareTargets: paths }),
  closeShareDialog: () => set({ shareTargets: null }),

  uploadTrigger: null,
  triggerUpload: (type, targetPath) => set({ uploadTrigger: { type, targetPath } }),
  clearUploadTrigger: () => set({ uploadTrigger: null }),

  initializeDataChannel: async (dc: RTCDataChannel) => {
      const { fs, setCurrentPath } = get();
      if (fs instanceof WebRTCSystem) {
          fs.setDataChannel(dc);
          await get().refreshQuickAccess();
          // 优先消费搜索结果触发的待导航路径（从关闭状态打开文件管理器时），否则默认跳转 Desktop
          const pendingNav = get().pendingNavPath;
          if (pendingNav) {
              await setCurrentPath(getParentPath(pendingNav) || 'root');
              set({ selectedPaths: [pendingNav], pendingNavPath: null });
          } else {
              // 恢复上次状态：先恢复视图模式，再尝试上次目录；目录失效则回退 Desktop(视图仍保留)
              const saved = loadFmState();
              if (saved) set({ viewMode: saved.viewMode });
              const navToDesktop = async () => {
                  const quickAccess = get().quickAccessItems;
                  const desktop = quickAccess.find(item => item.name.toLowerCase() === 'desktop');
                  if (desktop) await setCurrentPath(desktop.path);
                  else await get().refresh();
              };
              if (saved?.path) {
                  const ok = await setCurrentPath(saved.path);
                  if (!ok) await navToDesktop();  // 上次目录已失效，回退桌面
              } else {
                  await navToDesktop();  // 首次/无存档
              }
          }

          // Sync search engine status from backend (important for page refresh / reconnect)
          try {
              const statusRes = await fs.getSearchEngineStatus();
              useFileSearchStatusStore.getState().syncFromBackend(statusRes.status);
              // If engine is mid-initialization, start polling so progress updates live
              const inProgress = ['initializing', 'indexing', 'building_index', 'calculating_sizes'];
              if (inProgress.includes(statusRes.status)) {
                  console.log('[FileManager] Engine is mid-init after reconnect, starting status polling');
                  fs.startStatusPolling();
              }
          } catch (e) {
              console.warn('[FileManager] Failed to sync search engine status:', e);
          }
      }
  },

  // Actions
  setSelectedPaths: (paths: string[]) => set({ selectedPaths: paths }),
  setViewMode: (mode) => {
    set({ viewMode: mode });
    saveFmState(get().currentPath, mode);
  },

  goBack: async () => {
      const { history, historyIndex, setCurrentPath } = get();
      if (historyIndex > 0) {
          const newIndex = historyIndex - 1;
          set({ historyIndex: newIndex });
          await setCurrentPath(history[newIndex], false);
      }
  },
  goForward: async () => {
      const { history, historyIndex, setCurrentPath } = get();
      if (historyIndex < history.length - 1) {
          const newIndex = historyIndex + 1;
          set({ historyIndex: newIndex });
          await setCurrentPath(history[newIndex], false);
      }
  },
  goUp: async () => {
      const { currentPath, setCurrentPath } = get();
      if (!currentPath || currentPath === 'root') return;
      await setCurrentPath(getParentPath(currentPath), true);
  },

  setSortConfig: (config) => {
      set({ sortConfig: config });
      get().refresh();
  },
  setSearchQuery: (query) => {
      set({ searchQuery: query });
      get().refresh(); 
  },
  setSearchSettings: (settings) => {
      set((state) => ({ searchSettings: { ...state.searchSettings, ...settings } }));
      get().refresh();
  },

  // Global search methods
  setGlobalSearchMode: (mode) => set({ globalSearchMode: mode }),
  setGlobalSearchQuery: (query) => set({ globalSearchQuery: query }),
  openGlobalSearch: () => set({ isGlobalSearchOpen: true }),
  closeGlobalSearch: () => set({ isGlobalSearchOpen: false, globalSearchResults: [], globalSearchQuery: '', globalSearchError: null, globalSearchStatus: null, globalSearchTruncated: false }),
  executeGlobalSearch: async () => {
      const { globalSearchQuery, globalSearchMode, fs } = get();
      if (!globalSearchQuery.trim()) {
          set({ globalSearchResults: [], globalSearchError: null, globalSearchStatus: null });
          return;
      }
      set({ globalSearchLoading: true, globalSearchError: null, globalSearchStatus: null, globalSearchTruncated: false });

      const doSearch = async (): Promise<boolean> => {
          try {
              const mode = globalSearchMode === 'prefix' ? 0 : 1;
              const result = await (fs as any).globalSearch(globalSearchQuery, mode, GLOBAL_SEARCH_LIMIT, true);

              // Check if engine is still initializing
              if (result.status === 'initializing') {
                  set({
                      globalSearchError: result.message || t('searchInitializing'),
                      globalSearchStatus: 'initializing'
                  });
                  return false; // Need to retry
              }

              // Check if query is too short for contains mode
              if (result.status === 'query_too_short') {
                  set({
                      globalSearchResults: [],
                      globalSearchLoading: false,
                      globalSearchError: null, // Suppress error per user request
                      globalSearchStatus: null
                  });
                  return true; 
              }

              set({
                  globalSearchResults: result.results || [],
                  globalSearchLoading: false,
                  globalSearchError: null,
                  globalSearchStatus: null,
                  globalSearchTruncated: (result.results?.length ?? 0) >= GLOBAL_SEARCH_LIMIT,
              });
              return true; // Success
          } catch (e: any) {
              set({
                  globalSearchError: e.message || t('searchFailed'),
                  globalSearchLoading: false,
                  globalSearchResults: [],
                  globalSearchStatus: 'error'
              });
              return true; // Stop retrying on error
          }
      };

      // Try search, retry if initializing
      let success = await doSearch();
      if (!success) {
          // Retry every 2 seconds while initializing
          const retryInterval = setInterval(async () => {
              const currentQuery = get().globalSearchQuery;
              if (!currentQuery.trim() || !get().isGlobalSearchOpen) {
                  clearInterval(retryInterval);
                  set({ globalSearchLoading: false, globalSearchStatus: null });
                  return;
              }
              success = await doSearch();
              if (success) {
                  clearInterval(retryInterval);
              }
          }, 2000);
      }
  },
  navigateToSearchResult: async (path) => {
      const parentPath = getParentPath(path);
      get().closeGlobalSearch();
      if (get().isFileManagerOpen) {
          // 文件管理器已打开：直接导航（无重挂载，不会触发 initializeDataChannel 的 Desktop 自动跳转）
          await get().setCurrentPath(parentPath || 'root');
          set({ selectedPaths: [path] });
      } else {
          // 文件管理器未打开：暂存目标路径并请求开窗，导航由挂载后的 initializeDataChannel 消费，
          // 避免与其 Desktop 自动跳转竞态。
          set({ pendingNavPath: path });
          eventBus.emit('fm:open', undefined as never);
      }
  },

  openPreview: (file) => set({ previewFile: file, editorFile: null }),
  closePreview: () => set({ previewFile: null }),
  openEditor: (file) => set({ editorFile: file, previewFile: null }),
  closeEditor: () => set({ editorFile: null }),
  
  openFolderPicker: (title, onConfirm) => set({ folderPickerState: { isOpen: true, title, onConfirm } }),
  closeFolderPicker: () => set({ folderPickerState: null }),

  refreshQuickAccess: async () => {
      const items = await get().fs.getQuickAccess();
      set({ quickAccessItems: items });
  },
  pinFolder: async (path) => {
      await get().fs.pinToQuickAccess(path);
      await get().refreshQuickAccess();
  },
  unpinFolder: async (path) => {
      await get().fs.unpinFromQuickAccess(path);
      await get().refreshQuickAccess();
  },

  resolveConflict: (action, applyToAll) => {
      const { conflictState } = get();
      if (conflictState) {
          if (applyToAll) set({ conflictPolicy: `${action}_all` as any });
          conflictState.resolver(action, applyToAll);
          set({ conflictState: null });
      }
  },
  resetConflictPolicy: () => set({ conflictPolicy: 'ask' }),
  
  checkConflictsBatch: async (ops) => {
      const { fs, resetConflictPolicy } = get();
      resetConflictPolicy();
      const finalOps: { source: string, dest: string }[] = [];
      for (const op of ops) {
          let finalDest = op.dest;
          const name = getBaseName(finalDest);
          const parent = getParentPath(finalDest);
          let exists = false;
          try {
              const siblings = await fs.readdir(parent);
              exists = siblings.some(f => f.name === name);
          } catch {}
          if (exists) {
              const policy = get().conflictPolicy;
              let action = 'ask';
              if (policy === 'overwrite_all') action = 'overwrite';
              else if (policy === 'rename_all') action = 'rename';
              else if (policy === 'skip_all') action = 'skip';
              if (action === 'ask') {
                  action = await new Promise((resolve) => {
                      set({ conflictState: { sourceFile: name, targetPath: finalDest, resolver: (a) => resolve(a) }});
                  });
              }
              if (action === 'skip') continue;
              if (action === 'rename') {
                  const parts = name.split('.');
                  const ext = parts.length > 1 ? `.${parts.pop()}` : '';
                  const base = parts.join('.');
                  finalDest = joinPath(parent, `${base}_${Date.now()}${ext}`);
              }
          }
          finalOps.push({ source: op.source, dest: finalDest });
      }
      return finalOps;
  },

  downloadFiles: (paths) => {
      const run = async () => {
          const { files, fs } = get();
          let selectedFiles = files.filter(f => paths.includes(f.path));
          if (selectedFiles.length === 0 && paths.length > 0) {
              const resolved = await Promise.all(paths.map(async (path) => {
                  try {
                      await fs.readdir(path);
                      return { name: getBaseName(path) || path, path, size: 0, type: 'dir' as const };
                  } catch {
                      return { name: getBaseName(path) || path, path, size: 0, type: 'file' as const };
                  }
              }));
              selectedFiles = resolved;
          }
          if (selectedFiles.length === 0) return;

          // 先弹下载面板：复用 Ctrl+C 已验证的 filesAvailable → toast 路径。
          // 否则右键下载只发 downloadProgress，面板因 toast 为空而 return null 不渲染。
          const totalSize = selectedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
          eventBus.emit('clipboard:filesAvailable', {
              files: selectedFiles.map(f => ({
                  name: f.name,
                  path: f.path,
                  size: f.size || 0,
                  fileCount: 1,
                  isDir: f.type === 'dir',
              })),
              totalSize,
              fileCount: selectedFiles.length,
          });

          let fileName = 'download.zip';
          if (selectedFiles.length === 1) {
              const only = selectedFiles[0];
              fileName = only.type === 'dir' ? `${only.name}.zip` : only.name;
          }
          await fileDownloadService.downloadWithFiles(
              selectedFiles.map(f => ({ name: f.name, path: f.path, size: f.size, isDir: f.type === 'dir' })),
              fileName
          );
      };
      run().catch((e) => {
          const message = e instanceof Error ? e.message : String(e);
          alert(t('downloadFailed', { message }));
      });
  },
  copyLink: (path) => {
      // Since it's P2P, we don't have a direct download link usually.
      // But we can copy the path or some internal marker.
      navigator.clipboard.writeText(path);
      alert(t('pathCopied'));
  },
  copyPath: (path) => navigator.clipboard.writeText(path),
  unzip: async (path, dest) => {
      const { currentPath, fs, openFolderPicker, refresh } = get();
      const perform = async (target: string) => {
          try {
              await fs.unzip(path, target);
              await refresh();
          } catch (e) {
              const message = e instanceof Error ? e.message : String(e);
              alert(t('unzipFailed', { message }));
          }
      };
      if (dest === 'PICK_TARGET') openFolderPicker(t('extractTo'), perform);
      else perform(dest || currentPath);
  },
  
  toggleUploadManager: () => {
      set((state) => ({ isUploadManagerOpen: !state.isUploadManagerOpen }));
  },

  openUploadManager: () => {
      set({ isUploadManagerOpen: true });
  },
  
  addUploadTasks: (tasks, autoOpen = true) => {
      set((state) => ({ 
          uploadTasks: [...state.uploadTasks, ...tasks], 
          isUploadManagerOpen: autoOpen ? true : state.isUploadManagerOpen 
      }));
      get().processQueue(); 
  },
  updateUploadTask: (id, updates) => {
      const target = get().uploadTasks.find(t => t.id === id);
      set((state) => ({ uploadTasks: state.uploadTasks.map(t => t.id === id ? { ...t, ...updates } : t) }));
      if (['success', 'error', 'cancelled'].includes(updates.status || '')) {
          const batchId = target?.batchId;
          if (batchId) {
              const { uploadTasks, refreshedBatches, currentPath } = get();
              const batchTasks = uploadTasks.filter(t => t.batchId === batchId);
              const isComplete = batchTasks.length > 0 && batchTasks.every(t => ['success', 'error', 'cancelled'].includes(t.status));
              if (isComplete && !refreshedBatches.has(batchId)) {
                  const batchRootPath = batchTasks[0]?.batchRootPath;
                  if (batchRootPath && batchRootPath === currentPath) {
                      get().scheduleRefresh();
                  }
                  const next = new Set(refreshedBatches);
                  next.add(batchId);
                  set({ refreshedBatches: next });
              }
          }
          get().processQueue();
      }
  },
  processQueue: async () => {
      const { uploadTasks, fs, updateUploadTask } = get();
      // Sequential uploads to prevent buffer overflow - all uploads share the same DataChannel
      const MAX_CONCURRENT = 1;

      // Batch Logic
      const batches: Record<string, UploadTask[]> = {};
      uploadTasks.forEach(t => {
          if (!batches[t.batchId]) batches[t.batchId] = [];
          batches[t.batchId].push(t);
      });
      const sortedBatchIds = Object.keys(batches).sort((a, b) => batches[a][0].timestamp - batches[b][0].timestamp);

      let activeBatchId: string | null = null;
      for (const bid of sortedBatchIds) {
          const batch = batches[bid];
          if (!batch.every(t => ['success', 'error', 'cancelled'].includes(t.status))) {
              activeBatchId = bid;
              break;
          }
      }
      if (!activeBatchId) return;

      const activeBatchTasks = batches[activeBatchId];
      const uploading = activeBatchTasks.filter(t => t.status === 'uploading').length;
      if (uploading >= MAX_CONCURRENT) return;

      // Ensure channels are ready ONCE before starting uploads
      // This prevents race conditions when multiple uploads call ensureChannels simultaneously
      if (!fileTransferService.isBinaryUploadReady) {
          await fileTransferService.ensureChannelsAsync();
      }

      const pendingTasks = activeBatchTasks.filter(t => t.status === 'pending').slice(0, MAX_CONCURRENT - uploading);
      for (const task of pendingTasks) {
          const shouldCancel = () => {
              const current = get().uploadTasks.find(t => t.id === task.id);
              if (!current) return true;
              return current.status === 'cancelled' || get().cancelledBatches.has(task.batchId);
          };
          processUpload(task, fs, updateUploadTask, shouldCancel);
          // Small delay between starting uploads to prevent overwhelming
          await new Promise(r => setTimeout(r, 50));
      }
  },
  cancelUploadTask: (id) => {
      const task = get().uploadTasks.find(t => t.id === id);
      get().updateUploadTask(id, { status: 'cancelled' });
      set((state) => ({ uploadTasks: state.uploadTasks.filter(t => t.id !== id) }));
      if (task) {
          const { refreshedBatches } = get();
          if (refreshedBatches.has(task.batchId)) {
              const next = new Set(refreshedBatches);
              next.delete(task.batchId);
              set({ refreshedBatches: next });
          }
      }
      if (task && !task.isDirectory) {
          void get().fs.rm(task.path).catch(() => {});
      }
  },
  retryUploadTask: (id) => {
      const task = get().uploadTasks.find(t => t.id === id);
      if (task) {
          const { refreshedBatches } = get();
          if (refreshedBatches.has(task.batchId)) {
              const next = new Set(refreshedBatches);
              next.delete(task.batchId);
              set({ refreshedBatches: next });
          }
          get().updateUploadTask(id, { status: 'pending', progress: 0, error: undefined });
          get().processQueue(); // Use processQueue to resume
      }
  },
  requestCancelBatch: (batchId) => set({ cancelBatchConfirmation: { batchId } }),
  resolveCancelBatch: (choice) => {
      const { cancelBatchConfirmation, cancelBatch } = get();
      if (!cancelBatchConfirmation) return;
      if (choice === 'yes') cancelBatch(cancelBatchConfirmation.batchId, true);
      else if (choice === 'no') cancelBatch(cancelBatchConfirmation.batchId, false);
      set({ cancelBatchConfirmation: null });
  },
  cancelBatch: async (batchId, deleteUploaded) => {
      const { uploadTasks, updateUploadTask, fs, refresh, cancelledBatches, refreshedBatches } = get();
      cancelledBatches.add(batchId);
      const batchTasks = uploadTasks.filter(t => t.batchId === batchId);
      batchTasks.forEach(t => {
          if (t.status === 'pending' || t.status === 'uploading') updateUploadTask(t.id, { status: 'cancelled' });
      });
      // 先真正中止上传：通知 host 清空写队列并关闭所有上传写句柄，
      // 否则文件被 host 写句柄占用 / 残留 chunk 重写，删除会失败或复活
      fileTransferService.cancelBinaryUpload();
      await new Promise(r => setTimeout(r, 150));
      const failedDeletes: string[] = [];
      // host 对不存在的文件回 "Path not found"：pending/未上传文件本就不该在，
      // 视为无残留（删除目的已达成），不算失败；"Failed to remove"(占用/权限) 才算
      const isMissing = (e: any) => /not\s*found/i.test(e?.message || '');
      const incomplete = batchTasks.filter(t => t.status !== 'success');
      for (const t of incomplete) {
          if (!t.isDirectory) {
              try { await fs.rm(t.path); } catch (e: any) { if (!isMissing(e)) failedDeletes.push(t.path); }
          }
      }
      if (deleteUploaded) {
          const completed = batchTasks.filter(t => t.status === 'success');
          for (const t of completed) {
              try { await fs.rm(t.path, t.isDirectory); } catch (e: any) { if (!isMissing(e)) failedDeletes.push(t.path); }
          }
      }
      if (failedDeletes.length > 0) {
          const details = failedDeletes.slice(0, 5).map(p => p.split(/[\\/]/).pop()).join(', ')
              + (failedDeletes.length > 5 ? ` …(+${failedDeletes.length - 5})` : '');
          alert(t('deleteFailed', { count: failedDeletes.length, details }));
      }
      await refresh();
      set((state) => ({ uploadTasks: state.uploadTasks.filter(t => t.batchId !== batchId) }));
      if (refreshedBatches.has(batchId)) {
          const next = new Set(refreshedBatches);
          next.delete(batchId);
          set({ refreshedBatches: next });
      }
      get().processQueue();
  },
  retryBatch: (batchId) => {
      const { uploadTasks, updateUploadTask } = get();
      const { refreshedBatches } = get();
      if (refreshedBatches.has(batchId)) {
          const next = new Set(refreshedBatches);
          next.delete(batchId);
          set({ refreshedBatches: next });
      }
      const batchTasks = uploadTasks.filter(t => t.batchId === batchId);
      batchTasks.forEach(t => {
          if (t.status === 'error' || t.status === 'cancelled') updateUploadTask(t.id, { status: 'pending', progress: 0, error: undefined });
      });
      get().processQueue();
  },
  clearCompletedTasks: () => set((state) => ({ uploadTasks: state.uploadTasks.filter(t => t.status !== 'success' && t.status !== 'cancelled') })),
  
  scheduleRefresh: () => {
      const { refreshTimer, refresh } = get();
      if (refreshTimer) clearTimeout(refreshTimer);
      const timer = window.setTimeout(() => { refresh(); set({ refreshTimer: null }); }, 1000); 
      set({ refreshTimer: timer });
  },

  setCurrentPath: async (path: string, addToHistory = true) => {
    set({ isLoading: true, selectedPaths: [], renamingPath: null, searchQuery: '', files: [], totalFiles: 0, hasMore: false });
    try {
      const resolvedPath = await get().fs.resolvePath(path);
      const fs = get().fs as WebRTCSystem;

      // Update history first
      set((state) => {
        let newHistory = state.history;
        let newIndex = state.historyIndex;
        if (addToHistory) {
          newHistory = state.history.slice(0, state.historyIndex + 1);
          newHistory.push(resolvedPath);
          newIndex = newHistory.length - 1;
        }
        return { currentPath: resolvedPath, history: newHistory, historyIndex: newIndex };
      });

      // Use streaming for large directories
      if (fs.readdirStream) {
        let allFiles: FileStat[] = [];

        await fs.readdirStream(resolvedPath, (batch, total, offset, hasMore) => {
          // Append batch to files
          allFiles = [...allFiles, ...batch];

          // Update state incrementally - users see files appearing
          set({
            files: allFiles,
            totalFiles: total,
            hasMore: hasMore,
            isLoading: hasMore, // Keep loading indicator while streaming
          });
        });

        // Final update when complete
        set({ isLoading: false });
      } else {
        // Fallback to non-streaming
        const files = await get().fs.readdir(resolvedPath) ?? [];
        set({ files, totalFiles: files.length, hasMore: false, isLoading: false });
      }
      // 导航成功：持久化「上次状态」(目录 + 当前视图)，供下次打开恢复
      saveFmState(resolvedPath, get().viewMode);
      return true;
    } catch {
      set({ isLoading: false });
      return false;
    }
  },

  refresh: async () => {
    const { currentPath, searchQuery, searchSettings, sortConfig } = get();
    const fs = get().fs as WebRTCSystem;
    set({ isLoading: true, files: [], totalFiles: 0, hasMore: false });

    // Helper function to apply search filter and sort
    const applyFilterAndSort = (files: FileStat[]) => {
      let result = files;

      // Apply search filter
      if (searchQuery && result.length > 0) {
        const { regex, caseSensitive, wholeWord } = searchSettings;
        if (regex) {
          try {
            const re = new RegExp(searchQuery, caseSensitive ? '' : 'i');
            result = result.filter(f => re.test(f.name));
          } catch {}
        } else {
          const q = caseSensitive ? searchQuery : searchQuery.toLowerCase();
          result = result.filter(f => {
            const name = caseSensitive ? f.name : f.name.toLowerCase();
            return wholeWord ? name === q : name.includes(q);
          });
        }
      }

      // Sort files
      if (result.length > 0) {
        result.sort((a, b) => {
          let valA: any = a[sortConfig.key];
          let valB: any = b[sortConfig.key];

          if (sortConfig.key === 'name') {
            valA = valA?.toLowerCase?.() ?? '';
            valB = valB?.toLowerCase?.() ?? '';
          }

          if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
          if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        });
      }

      return result;
    };

    try {
      // Use streaming for large directories
      if (fs.readdirStream) {
        let allFiles: FileStat[] = [];

        await fs.readdirStream(currentPath, (batch, total, offset, hasMore) => {
          // Append batch to accumulated files
          allFiles = [...allFiles, ...batch];

          // Apply filter and sort to current accumulated files
          const processed = applyFilterAndSort(allFiles);

          // Update state incrementally
          set({
            files: processed,
            totalFiles: total,
            hasMore: hasMore,
            isLoading: hasMore,
          });
        });

        // Final sort on complete data
        const finalFiles = applyFilterAndSort(allFiles);
        set({ files: finalFiles, isLoading: false });
      } else {
        // Fallback to non-streaming
        let files = await fs.readdir(currentPath) ?? [];
        files = applyFilterAndSort(files);
        set({ files, totalFiles: files.length, hasMore: false, isLoading: false });
      }
    } catch {
      set({ isLoading: false });
    }
  },

  createNewFolder: async (parentPath) => {
    try {
      const { files, fs } = get();
      const baseName = t('newFolder');
      let name = baseName;
      let i = 2;
      while (files.some(f => f.name === name)) name = `${baseName} (${i++})`;

      const newPath = parentPath === 'root' ? name : joinPath(parentPath, name);

      await fs.mkdir(newPath);
      await get().refresh();

      // Find the actual path from refreshed files (backend may normalize the path)
      const { files: refreshedFiles } = get();
      const createdFolder = refreshedFiles.find(f => f.name === name && f.type === 'dir');
      const actualPath = createdFolder?.path ?? newPath;

      set({ renamingPath: actualPath, selectedPaths: [actualPath] });
    } catch { /* ignore */ }
  },
  startRenaming: (path) => set({ renamingPath: path }),
  cancelRenaming: () => set({ renamingPath: null }),
  endRenaming: async (oldPath, newName) => {
    set({ renamingPath: null });
    if (!newName.trim()) return;

    const parent = getParentPath(oldPath);
    const newPath = parent === 'root' ? newName : joinPath(parent, newName);

    if (oldPath === newPath) return;
    try { await get().fs.rename(oldPath, newPath); await get().refresh(); } catch (e) { alert(t('renameFailed')); }
  },
  deleteFiles: async (paths) => {
    const { fs, refresh, files } = get();

    // Check which paths are non-empty directories
    const nonEmptyDirs: string[] = [];
    for (const path of paths) {
      const file = files.find(f => f.path === path);
      if (file?.type === 'dir') {
        try {
          const contents = await fs.readdir(path);
          if (contents.length > 0) {
            nonEmptyDirs.push(path);
          }
        } catch {
          // If we can't read it, assume it might have contents
        }
      }
    }

    // Build confirmation message
    let message = t('deleteConfirm', { count: paths.length });
    if (nonEmptyDirs.length > 0) {
      const names = nonEmptyDirs.map(p => `"${getBaseName(p)}"`).join(', ');
      message = nonEmptyDirs.length === 1
        ? t('deleteConfirmNonEmptySingle', { count: paths.length, names })
        : t('deleteConfirmNonEmptyMultiple', { count: paths.length, names });
    }

    if (!confirm(message)) return;

    // Proceed with deletion (use recursive for directories)
    const failedPaths: { path: string; error: string }[] = [];

    for (const path of paths) {
      try {
        const isDir = files.find(f => f.path === path)?.type === 'dir';
        await fs.rm(path, isDir); // Always use recursive for directories
      } catch (e: any) {
        failedPaths.push({ path, error: e?.message || String(e) });
      }
    }

    await refresh();
    set({ selectedPaths: [] });

    if (failedPaths.length > 0) {
      const details = failedPaths.map(f => `${getBaseName(f.path)}: ${f.error}`).join('\n');
      alert(t('deleteFailed', { count: failedPaths.length, details }));
    }
  },
  copyFiles: (cut) => {
    const { selectedPaths } = get();
    if (selectedPaths.length > 0) set({ clipboard: { paths: selectedPaths, cut } });
  },
  pasteFiles: async () => {
    const { clipboard, currentPath, fs, checkConflictsBatch } = get();
    if (!clipboard || !clipboard.paths.length) return;
    try {
      const ops = clipboard.paths.map(src => ({
        source: src,
        dest: joinPath(currentPath, getBaseName(src))
      }));
      const finalOps = await checkConflictsBatch(ops);
      for (const op of finalOps) {
        if (clipboard.cut) await fs.move(op.source, op.dest);
        else await fs.copy(op.source, op.dest);
      }
      if (clipboard.cut) set({ clipboard: null });
      await get().refresh();
    } catch (e) { alert(t('pasteFailed')); }
  },
  uploadFiles: async (items: { file?: File, relativePath?: string, isDirectory?: boolean }[], targetPath?: string) => {
    const { currentPath, addUploadTasks, resetConflictPolicy, fs } = get();
    const rootDest = targetPath || currentPath;
    const batchId = Math.random().toString(36).substr(2, 9);
    const now = Date.now();
    // Size limits removed - no restrictions on file/batch size
    resetConflictPolicy();
    const dirCache: Record<string, Set<string>> = {};
    const getDirSet = async (dir: string) => {
        if (!dirCache[dir]) {
            try { const f = await fs.readdir(dir); dirCache[dir] = new Set(f.map(x => x.name)); }
            catch { dirCache[dir] = new Set(); }
        }
        return dirCache[dir];
    };
    const CHUNK = 50;
    for (let i = 0; i < items.length; i += CHUNK) {
        if (get().cancelledBatches.has(batchId)) {
            const { uploadTasks, updateUploadTask } = get();
            const batchTasks = uploadTasks.filter(t => t.batchId === batchId && (t.status === 'pending' || t.status === 'uploading'));
            batchTasks.forEach(t => updateUploadTask(t.id, { status: 'cancelled' }));
            break;
        }
        const chunk = items.slice(i, i + CHUNK);
        const tasks: UploadTask[] = [];
        for (const item of chunk) {
            const rel = item.relativePath || (item.file ? item.file.name : '');
            let full = rootDest === 'root' ? rel : joinPath(rootDest, rel);
            if (!item.isDirectory && item.file) {
                const parent = getParentPath(full);
                const name = getBaseName(full);
                const siblings = await getDirSet(parent);
                if (siblings.has(name)) {
                    let action = 'ask';
                    const policy = get().conflictPolicy;
                    if (policy === 'overwrite_all') action = 'overwrite';
                    else if (policy === 'rename_all') action = 'rename';
                    else if (policy === 'skip_all') action = 'skip';
                    if (action === 'ask') {
                        action = await new Promise((resolve) => {
                            set({ conflictState: { sourceFile: name, targetPath: full, resolver: (a) => resolve(a) }});
                        });
                    }
                    if (action === 'skip') continue;
                    if (action === 'rename') {
                        const nameParts = name.split('.');
                        const ext = nameParts.length > 1 ? `.${nameParts.pop()}` : '';
                        const baseName = nameParts.join('.');
                        full = joinPath(parent, `${baseName}_${Date.now()}${ext}`);
                    }
                }
            }
            const size = item.file ? item.file.size : 0;
            tasks.push({
                id: Math.random().toString(36).substr(2, 9), batchId, batchRootPath: rootDest, file: item.file, name: getBaseName(full),
                size, path: full, progress: 0, status: 'pending',
                isDirectory: !!item.isDirectory, timestamp: now
            });
        }
        if (tasks.length > 0) { addUploadTasks(tasks, i === 0); await new Promise(r => setTimeout(r, 0)); }
    }
  }
}));

const processUpload = async (
    task: UploadTask,
    fs: IFileSystem,
    updateTask: any,
    shouldCancel: (() => boolean) | undefined,
    retryAttempt: number = 0
) => {
    const MAX_RETRIES = 2;
    const CANCELLED_ERROR = 'UPLOAD_CANCELLED';
    const throwIfCancelled = () => {
        if (!shouldCancel) return;
        if (shouldCancel()) {
            updateTask(task.id, { status: 'cancelled' });
            throw new Error(CANCELLED_ERROR);
        }
    };

    if (task.status === 'error') { updateTask(task.id, {}); return; }
    throwIfCancelled();
    updateTask(task.id, { status: 'uploading' });
    const ensureDir = async (p: string) => { try { await fs.mkdir(p); } catch {} };

    // 滑动窗口测速 + UI 刷新节流（仅节流字节/速度上报；状态变更仍走原 updateTask 不受影响）
    const speedSamples: Array<{ ts: number; bytes: number }> = [];
    let lastEmitTs = 0;
    const reportProgress = (bytesWritten: number, totalBytes: number) => {
        const now = Date.now();
        speedSamples.push({ ts: now, bytes: bytesWritten });
        const cutoff = now - 1500;
        while (speedSamples.length > 0 && speedSamples[0].ts < cutoff) speedSamples.shift();
        let speed = 0;
        if (speedSamples.length >= 2) {
            const f = speedSamples[0], l = speedSamples[speedSamples.length - 1];
            const dt = l.ts - f.ts;
            if (dt > 0) speed = ((l.bytes - f.bytes) * 1000) / dt;
        }
        if (now - lastEmitTs < 250) return;
        lastEmitTs = now;
        const progress = totalBytes === 0 ? 100 : Math.round((bytesWritten / totalBytes) * 100);
        updateTask(task.id, { progress, uploadedBytes: bytesWritten, speed });
    };

    try {
        if (task.isDirectory) {
            throwIfCancelled();
            await ensureDir(task.path);
        updateTask(task.id, { status: 'success', progress: 100 });
        return;
        }

        // Ensure parent directory exists
        const parentPath = getParentPath(task.path);
        if (parentPath && parentPath !== 'root') {
            throwIfCancelled();
            await ensureDir(parentPath);
        }

        const file = task.file;
        if (!file) throw new Error('File object is missing');

        const total = task.size;

        // Channel is already ensured by processQueue, just check if it's ready
        // Try binary upload first (fastest - no base64, dedicated channel)
        if (fileTransferService.isBinaryUploadReady) {
            await fileTransferService.uploadFileBinary(
                task.path,
                file,
                (bytesWritten, totalBytes) => {
                    reportProgress(bytesWritten, totalBytes);
                },
                shouldCancel
            );
        } else {
            // Fallback to JSON-RPC with base64 (slower, use smaller chunks)
            const CHUNK_SIZE = 128 * 1024; // 128KB chunks for stability
            let offset = 0;
            let isFirst = true;

            // Handle empty files: create with empty content
            if (total === 0) {
                await fs.writeFile(task.path, '', 0, true, true);
                updateTask(task.id, { progress: 100, uploadedBytes: 0 });
            } else {
                while (offset < total) {
                    throwIfCancelled();
                    const chunk = file.slice(offset, offset + CHUNK_SIZE);
                    const buffer = await chunk.arrayBuffer();

                    // Convert to base64
                    const bytes = new Uint8Array(buffer);
                    let binary = '';
                    const batchSize = 8192;
                    for (let i = 0; i < bytes.length; i += batchSize) {
                        const batch = bytes.subarray(i, i + batchSize);
                        for (let j = 0; j < batch.length; j++) {
                            binary += String.fromCharCode(batch[j]);
                        }
                    }
                    await fs.writeFile(task.path, btoa(binary), offset, isFirst, isFirst);

                    offset += chunk.size;
                    isFirst = false;

                    reportProgress(offset, total);

                    // Small delay between chunks to prevent overwhelming the channel
                    throwIfCancelled();
                    await new Promise(r => setTimeout(r, 10));
                }
            }
        }

        throwIfCancelled();
        updateTask(task.id, { status: 'success', progress: 100, uploadedBytes: task.size, speed: 0 });
    } catch (e: any) {
        if (e?.message === CANCELLED_ERROR) {
            return;
        }
        // Check if this is a channel-related error that might be recoverable
        const isChannelError = e.message?.includes('DataChannel') ||
                              e.message?.includes('not open') ||
                              e.message?.includes('closed');
        // host 写线程偶发卡顿/共享冲突导致的瞬时上传错误，自动重试（无需用户手动点）
        const isTransientUploadError = e.message?.includes('ACK timeout') ||
                              e.message?.includes('Server error');

        if ((isChannelError || isTransientUploadError) && retryAttempt < MAX_RETRIES) {
            updateTask(task.id, { status: 'pending', progress: 0, uploadedBytes: 0, speed: 0 });
            await new Promise(r => setTimeout(r, 500 * (retryAttempt + 1)));
            await fileTransferService.ensureChannelsAsync();
            await processUpload(task, fs, updateTask, shouldCancel, retryAttempt + 1);
        } else {
            updateTask(task.id, { status: 'error', error: e.message });
        }
    }
};
